import { useState, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || window.location.origin;

const PREFERRED_MIME = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function getSupportedMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of PREFERRED_MIME) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export default function useCallRecording() {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const destRef = useRef(null);
  const ctxRef = useRef(null);
  const startedAtRef = useRef(0);

  const startRecording = useCallback((localStream, remoteStream) => {
    const mimeType = getSupportedMime();
    if (!mimeType) {
      console.warn('[CallRecording] MediaRecorder not supported');
      return;
    }

    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const dest = ctx.createMediaStreamDestination();
      destRef.current = dest;

      if (localStream) {
        const localSrc = ctx.createMediaStreamSource(localStream);
        localSrc.connect(dest);
      }
      if (remoteStream) {
        const remoteSrc = ctx.createMediaStreamSource(remoteStream);
        remoteSrc.connect(dest);
      }

      chunksRef.current = [];
      const recorder = new MediaRecorder(dest.stream, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      startedAtRef.current = Date.now();
      recorder.start(1000);
      setIsRecording(true);
    } catch (e) {
      console.error('[CallRecording] Start failed:', e);
    }
  }, []);

  const stopRecording = useCallback(async (callId, sessionId, callerName) => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        setIsRecording(false);
        return resolve(null);
      }

      recorder.onstop = async () => {
        setIsRecording(false);
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];

        if (ctxRef.current) {
          ctxRef.current.close().catch(() => {});
          ctxRef.current = null;
        }

        try {
          const form = new FormData();
          form.append('recording', blob, `call_${callId}.webm`);
          form.append('callId', callId || '');
          form.append('sessionId', sessionId || '');
          form.append('callerName', callerName || '');
          const durationSec = startedAtRef.current
            ? Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000))
            : 0;
          form.append('duration', String(durationSec));

          const resp = await fetch(`${API_BASE}/api/recordings/upload`, {
            method: 'POST',
            body: form,
          });
          const data = await resp.json();
          if (data.success && data.recording?.url) {
            setRecordingUrl(data.recording.url);
            resolve(data.recording);
            return;
          }
        } catch (e) {
          console.error('[CallRecording] Upload failed:', e);
        }

        const localUrl = URL.createObjectURL(blob);
        setRecordingUrl(localUrl);
        resolve({ url: localUrl, local: true });
      };

      recorder.stop();
    });
  }, []);

  return { isRecording, recordingUrl, startRecording, stopRecording };
}
